import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShowChart as ChartIcon,
  Close as CloseIcon,
  ZoomOutMap as ExpandIcon
} from '@mui/icons-material';
import { ccyFormat } from '../../helpers/helper';

const GrowthAnalysis = ({ stats }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedChart, setExpandedChart] = useState(null);

  if (!stats || !stats.daily_sales_data || stats.daily_sales_data.length === 0) {
    return null;
  }

  // Calcular métricas de crecimiento
  const getGrowthMetrics = () => {
    const data = stats.daily_sales_data;
    if (data.length < 2) return null;

    // Dividir en dos períodos iguales
    // Ejemplo: Si tienes datos del 1 al 30 de diciembre (30 días)
    // Período 1: Del 1 al 15 (primeros 15 días)
    // Período 2: Del 16 al 30 (últimos 15 días)
    const midPoint = Math.floor(data.length / 2);
    const firstHalf = data.slice(0, midPoint);
    const secondHalf = data.slice(midPoint);

    const calcPeriodMetrics = (period) => {
      const totalSales = period.reduce((sum, day) => sum + (day.total_sales || 0), 0);
      const totalRevenue = period.reduce((sum, day) => sum + (day.revenue || 0), 0);
      const avgSales = totalSales / period.length;
      const avgRevenue = totalRevenue / period.length;
      const avgTicket = totalSales > 0 ? totalRevenue / totalSales : 0;
      
      return { totalSales, totalRevenue, avgSales, avgRevenue, avgTicket, days: period.length };
    };

    const first = calcPeriodMetrics(firstHalf);
    const second = calcPeriodMetrics(secondHalf);

    // Calcular porcentajes de crecimiento
    // Fórmula: ((Valor Nuevo - Valor Viejo) / Valor Viejo) * 100
    // Ejemplo: Si tenías 100 ventas y ahora tienes 120:
    // ((120 - 100) / 100) * 100 = 20% de crecimiento
    const salesGrowth = first.totalSales > 0 
      ? ((second.totalSales - first.totalSales) / first.totalSales) * 100 
      : 0;
    
    const revenueGrowth = first.totalRevenue > 0
      ? ((second.totalRevenue - first.totalRevenue) / first.totalRevenue) * 100
      : 0;
    
    const avgSalesGrowth = first.avgSales > 0
      ? ((second.avgSales - first.avgSales) / first.avgSales) * 100
      : 0;
    
    const ticketGrowth = first.avgTicket > 0
      ? ((second.avgTicket - first.avgTicket) / first.avgTicket) * 100
      : 0;

    return {
      first,
      second,
      salesGrowth,
      revenueGrowth,
      avgSalesGrowth,
      ticketGrowth
    };
  };

  // Calcular promedios móviles para suavizar la tendencia
  // El promedio móvil toma los últimos 3 días y calcula el promedio
  // Ejemplo: Para el día 5, promedia los días 3, 4 y 5
  // Esto elimina las fluctuaciones diarias y muestra la tendencia real
  // Si tuviste 10, 50, 15 ventas -> el promedio móvil muestra 25 (más estable)
  const getMovingAverage = (data, key, window = 3) => {
    return data.map((item, index, array) => {
      const start = Math.max(0, index - window + 1);
      const subset = array.slice(start, index + 1);
      const avg = subset.reduce((sum, d) => sum + (d[key] || 0), 0) / subset.length;
      
      return {
        ...item,
        [`${key}_ma`]: avg
      };
    });
  };

  // Preparar datos con promedios móviles
  const dataWithMA = getMovingAverage(
    getMovingAverage(stats.daily_sales_data, 'total_sales', 3),
    'revenue',
    3
  );

  // Calcular tendencia acumulada
  // Va sumando día tras día para ver el crecimiento total
  // Ejemplo:
  // Día 1: 10 ventas -> Acumulado: 10
  // Día 2: 15 ventas -> Acumulado: 25 (10+15)
  // Día 3: 8 ventas  -> Acumulado: 33 (10+15+8)
  // Este gráfico siempre sube (o se mantiene), mostrando el crecimiento total
  const getCumulativeData = () => {
    let cumulativeSales = 0;
    let cumulativeRevenue = 0;

    return stats.daily_sales_data.map(day => {
      cumulativeSales += day.total_sales || 0;
      cumulativeRevenue += day.revenue || 0;

      return {
        date: day.date,
        cumulativeSales,
        cumulativeRevenue
      };
    });
  };

  const cumulativeData = getCumulativeData();
  const growthMetrics = getGrowthMetrics();

  const GrowthCard = ({ title, value, growth, color, prefix = '', suffix = '' }) => {
    const isPositive = growth >= 0;
    const Icon = isPositive ? TrendingUpIcon : TrendingDownIcon;
    
    return (
      <Card elevation={0} sx={{ 
        border: '1px solid #e0e0e0',
        WebkitTapHighlightColor: 'transparent'
      }}>
        <CardContent>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color, mb: 1 }}>
            {prefix}{typeof value === 'number' ? value.toFixed(1) : value}{suffix}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Icon 
              sx={{ 
                fontSize: 18, 
                color: isPositive ? '#4caf50' : '#f44336' 
              }} 
            />
            <Typography 
              variant="body2" 
              fontWeight={600}
              sx={{ color: isPositive ? '#4caf50' : '#f44336' }}
            >
              {isPositive ? '+' : ''}{growth.toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              vs período anterior
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('Ingresos') || entry.name.includes('Revenue') 
                ? `$${ccyFormat(entry.value)}` 
                : entry.value.toFixed(1)}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{
      WebkitTapHighlightColor: 'transparent',
      '& .recharts-wrapper': {
        WebkitTapHighlightColor: 'transparent',
      }
    }}>
      <Paper elevation={0} sx={{ 
        p: isMobile ? 2 : 3, 
        mb: 4, 
        borderRadius: 3, 
        border: '1px solid #e0e0e0',
        WebkitTapHighlightColor: 'transparent'
      }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <ChartIcon sx={{ color: '#9c27b0', fontSize: isMobile ? 24 : 28 }} />
          <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600}>
            Análisis de Crecimiento
          </Typography>
        </Stack>
        <Divider sx={{ mb: 3 }} />

        {/* Explicación de cómo funciona */}
        <Box mb={3} p={2} sx={{ 
          bgcolor: '#f3e5f5', 
          borderRadius: 2,
          border: '1px solid #ce93d8',
          WebkitTapHighlightColor: 'transparent'
        }}>
          <Typography variant="body2" fontWeight={600} color="#7b1fa2" gutterBottom>
            📊 ¿Cómo se calcula el crecimiento?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            <strong>Comparación de Períodos:</strong> Dividimos tus datos en 2 mitades iguales y las comparamos.
          </Typography>
          {growthMetrics && (
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              mt: 1
            }}>
              <Box sx={{ 
                flex: 1, 
                p: 1, 
                bgcolor: 'rgba(33, 150, 243, 0.1)', 
                borderRadius: 1,
                border: '1px solid rgba(33, 150, 243, 0.3)'
              }}>
                <Typography variant="caption" fontWeight={600} color="#1976d2">
                  📅 Período 1 (Anterior)
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  Primeros {growthMetrics.first.days} días
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {growthMetrics.first.totalSales} ventas
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#9c27b0',
                fontSize: isMobile ? 16 : 24,
                fontWeight: 'bold'
              }}>
                →
              </Box>
              
              <Box sx={{ 
                flex: 1, 
                p: 1, 
                bgcolor: 'rgba(76, 175, 80, 0.1)', 
                borderRadius: 1,
                border: '1px solid rgba(76, 175, 80, 0.3)'
              }}>
                <Typography variant="caption" fontWeight={600} color="#388e3c">
                  📅 Período 2 (Reciente)
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  Últimos {growthMetrics.second.days} días
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {growthMetrics.second.totalSales} ventas
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Métricas de Crecimiento */}
        {growthMetrics && (
          <Grid container spacing={isMobile ? 2 : 3} mb={4}>
            <Grid item xs={6} md={3}>
              <GrowthCard
                title="Ventas Totales"
                value={growthMetrics.second.totalSales}
                growth={growthMetrics.salesGrowth}
                color="#2196f3"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <GrowthCard
                title="Ingresos Totales"
                value={growthMetrics.second.totalRevenue}
                growth={growthMetrics.revenueGrowth}
                color="#4caf50"
                prefix="$"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <GrowthCard
                title="Promedio Diario"
                value={growthMetrics.second.avgSales}
                growth={growthMetrics.avgSalesGrowth}
                color="#ff9800"
                suffix=" ventas/día"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <GrowthCard
                title="Ticket Promedio"
                value={growthMetrics.second.avgTicket}
                growth={growthMetrics.ticketGrowth}
                color="#9c27b0"
                prefix="$"
              />
            </Grid>
          </Grid>
        )}

        {/* Gráfico de Tendencia con Promedio Móvil */}
        <Box sx={{ position: 'relative', mb: 4 }}>
          {isMobile && (
            <IconButton
              onClick={() => setExpandedChart('trend')}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                zIndex: 1,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: 1,
                WebkitTapHighlightColor: 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                }
              }}
              size="small"
            >
              <ExpandIcon fontSize="small" />
            </IconButton>
          )}
          
          <Typography variant={isMobile ? "body1" : "subtitle1"} fontWeight={600} gutterBottom>
            Tendencia de Ventas e Ingresos (con promedio móvil)
          </Typography>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block" mb={1}>
            <strong>¿Qué muestra?</strong> Las líneas suavizadas muestran la tendencia real eliminando las fluctuaciones del día a día.
          </Typography>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block" mb={2}>
            • <strong style={{ color: 'rgba(33, 150, 243, 0.3)' }}>Líneas transparentes:</strong> Datos reales de cada día (pueden variar mucho)<br/>
            • <strong style={{ color: '#2196f3' }}>Líneas gruesas:</strong> Promedio de los últimos 3 días (muestra si vas en subida o bajada)<br/>
            <em>Ejemplo: Si hoy vendiste 5, ayer 15 y anteayer 10 → el promedio móvil muestra 10</em>
          </Typography>
          
          <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
            <LineChart data={dataWithMA}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: isMobile ? 9 : 11 }}
                tickFormatter={(date) => {
                  const [year, month, day] = date.split('-').map(Number);
                  const d = new Date(year, month - 1, day);
                  return `${d.getDate()}/${d.getMonth() + 1}`;
                }}
              />
              <YAxis yAxisId="left" tick={{ fontSize: isMobile ? 9 : 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: isMobile ? 9 : 11 }} />
              <Tooltip content={<CustomTooltip />} />
              {!isMobile && <Legend />}
              
              {/* Datos reales con transparencia */}
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="total_sales" 
                name="Ventas (real)" 
                stroke="#2196f3" 
                strokeWidth={1}
                dot={false}
                opacity={0.3}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="revenue" 
                name="Ingresos (real)" 
                stroke="#4caf50" 
                strokeWidth={1}
                dot={false}
                opacity={0.3}
              />
              
              {/* Promedio móvil destacado */}
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="total_sales_ma" 
                name="Ventas (tendencia)" 
                stroke="#2196f3" 
                strokeWidth={3}
                dot={{ r: 2 }}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="revenue_ma" 
                name="Ingresos (tendencia)" 
                stroke="#4caf50" 
                strokeWidth={3}
                dot={{ r: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {/* Gráfico Acumulativo */}
        <Box sx={{ position: 'relative' }}>
          {isMobile && (
            <IconButton
              onClick={() => setExpandedChart('cumulative')}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                zIndex: 1,
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: 1,
                WebkitTapHighlightColor: 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                }
              }}
              size="small"
            >
              <ExpandIcon fontSize="small" />
            </IconButton>
          )}
          
          <Typography variant={isMobile ? "body1" : "subtitle1"} fontWeight={600} gutterBottom>
            Crecimiento Acumulado
          </Typography>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block" mb={1}>
            <strong>¿Qué muestra?</strong> El total de ventas e ingresos sumados día tras día.
          </Typography>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block" mb={2}>
            • Este gráfico <strong>siempre sube</strong> (o se mantiene), nunca baja<br/>
            • <strong>Pendiente pronunciada =</strong> crecimiento rápido<br/>
            • <strong>Pendiente suave =</strong> crecimiento lento<br/>
            • <strong>Línea horizontal =</strong> sin ventas en ese período<br/>
            <em>Ejemplo: Día 1: 10 ventas | Día 2: +15 = 25 total | Día 3: +8 = 33 total</em>
          </Typography>
          
          <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
            <AreaChart data={cumulativeData}>
              <defs>
                <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#2196f3" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: isMobile ? 9 : 11 }}
                tickFormatter={(date) => {
                  const [year, month, day] = date.split('-').map(Number);
                  const d = new Date(year, month - 1, day);
                  return `${d.getDate()}/${d.getMonth() + 1}`;
                }}
              />
              <YAxis yAxisId="left" tick={{ fontSize: isMobile ? 9 : 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: isMobile ? 9 : 11 }} />
              <Tooltip content={<CustomTooltip />} />
              {!isMobile && <Legend />}
              <Area 
                yAxisId="left"
                type="monotone" 
                dataKey="cumulativeSales" 
                name="Ventas Acumuladas" 
                stroke="#2196f3" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorSales)" 
              />
              <Area 
                yAxisId="right"
                type="monotone" 
                dataKey="cumulativeRevenue" 
                name="Ingresos Acumulados" 
                stroke="#4caf50" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorRevenue)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        {/* Insight de Crecimiento */}
        {growthMetrics && (
          <Box mt={3} p={isMobile ? 1.5 : 2} sx={{ 
            bgcolor: growthMetrics.salesGrowth >= 0 ? '#e8f5e9' : '#ffebee', 
            borderRadius: 2,
            WebkitTapHighlightColor: 'transparent'
          }}>
            <Typography variant="body2" color="text.secondary" gutterBottom fontSize={isMobile ? '0.8rem' : undefined}>
              📈 <strong>Análisis de Período:</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" fontSize={isMobile ? '0.7rem' : undefined}>
              • Comparando los últimos <strong>{growthMetrics.second.days} días</strong> vs los <strong>{growthMetrics.first.days} días</strong> anteriores<br/>
              • Las ventas {growthMetrics.salesGrowth >= 0 ? 'crecieron' : 'disminuyeron'} un <strong>{Math.abs(growthMetrics.salesGrowth).toFixed(1)}%</strong><br/>
              • Los ingresos {growthMetrics.revenueGrowth >= 0 ? 'crecieron' : 'disminuyeron'} un <strong>{Math.abs(growthMetrics.revenueGrowth).toFixed(1)}%</strong><br/>
              • El ticket promedio {growthMetrics.ticketGrowth >= 0 ? 'aumentó' : 'disminuyó'} un <strong>{Math.abs(growthMetrics.ticketGrowth).toFixed(1)}%</strong>
              {growthMetrics.ticketGrowth > 0 && ' (¡excelente! tus clientes compran más por venta)'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Modal de Tendencia Expandido */}
      <Dialog
        open={expandedChart === 'trend'}
        onClose={() => setExpandedChart(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            WebkitTapHighlightColor: 'transparent',
            bgcolor: '#fafafa'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Tendencia de Ventas e Ingresos
            </Typography>
            <IconButton 
              onClick={() => setExpandedChart(null)}
              sx={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block" mb={2}>
            Las líneas suavizadas muestran la tendencia general eliminando fluctuaciones diarias
          </Typography>
          
          <Box sx={{ overflowX: 'auto', overflowY: 'hidden', pb: 2 }}>
            <Box sx={{ minWidth: Math.max(800, dataWithMA.length * 50) }}>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={dataWithMA}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(date) => {
                      const [year, month, day] = date.split('-').map(Number);
                      const d = new Date(year, month - 1, day);
                      return `${d.getDate()}/${d.getMonth() + 1}`;
                    }}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="total_sales" 
                    name="Ventas (real)" 
                    stroke="#2196f3" 
                    strokeWidth={1}
                    dot={false}
                    opacity={0.3}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="revenue" 
                    name="Ingresos (real)" 
                    stroke="#4caf50" 
                    strokeWidth={1}
                    dot={false}
                    opacity={0.3}
                  />
                  
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="total_sales_ma" 
                    name="Ventas (tendencia)" 
                    stroke="#2196f3" 
                    strokeWidth={3}
                    dot={{ r: 2 }}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="revenue_ma" 
                    name="Ingresos (tendencia)" 
                    stroke="#4caf50" 
                    strokeWidth={3}
                    dot={{ r: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Modal de Acumulado Expandido */}
      <Dialog
        open={expandedChart === 'cumulative'}
        onClose={() => setExpandedChart(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            WebkitTapHighlightColor: 'transparent',
            bgcolor: '#fafafa'
          }
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Crecimiento Acumulado
            </Typography>
            <IconButton 
              onClick={() => setExpandedChart(null)}
              sx={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block" mb={2}>
            Visualiza el crecimiento total de la tienda desde el inicio del período
          </Typography>
          
          <Box sx={{ overflowX: 'auto', overflowY: 'hidden', pb: 2 }}>
            <Box sx={{ minWidth: Math.max(800, cumulativeData.length * 50) }}>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={cumulativeData}>
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#2196f3" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(date) => {
                      const [year, month, day] = date.split('-').map(Number);
                      const d = new Date(year, month - 1, day);
                      return `${d.getDate()}/${d.getMonth() + 1}`;
                    }}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="cumulativeSales" 
                    name="Ventas Acumuladas" 
                    stroke="#2196f3" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorSales)" 
                  />
                  <Area 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="cumulativeRevenue" 
                    name="Ingresos Acumulados" 
                    stroke="#4caf50" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorRevenue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GrowthAnalysis;
